import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import { Breadcrumb } from '@els/els-react--breadcrumb';
import { Flyout } from '@els/els-react--flyout';
import { dpitems } from '@Constants';
import { paths } from '@AppRoutes/constants';
import {
  isBackToQueryVisibleFn,
  isBackToQueryBuilderFn,
  shouldDisplayPreviewResultsBreadcrumbFn,
} from '@Containers/Breadcrumbs/BreadcrumbsSelectors';
import StructureDataContainer from '@Containers/Breadcrumbs/BreadcrumbTooltip/StructureData';
import { recordCountOrTotalResultFromBreadcrumb } from '@Containers/PatentFamilyClustering/selectors';
import { formatNumberWithPostfix, replaceCitations } from '@Utils/String';
import { BACK_TO_QUERY_QUICK_SEARCH_ACTIONS } from '@Containers/Breadcrumbs/BreadcrumbsConstants';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';

import './ResultsBreadcrumbs.scss';

// Workaround to allow clicking on buttons inside tooltip without calling breadcrumb link
// TODO update after STYLE-1804 is done
function isClickWithinTooltip(e) {
  return e?.target?.closest('.c-els-flyout');
}

// Just label
function getBreadcrumbLabel(breadcrumb) {
  if (breadcrumb.get('isHistoryBreadcrumb')) return 'From history';
  if (breadcrumb.get('batchSearch')) return 'Batch Load';

  const totalResults = recordCountOrTotalResultFromBreadcrumb(breadcrumb) || 0;
  const contextLabel = capitalize(replaceCitations(breadcrumb.get('context', '')));
  return `${formatNumberWithPostfix(totalResults, true, 2)} ${contextLabel}`;
}

// Label wrapped with tooltip
function getBreadcrumbContent(breadcrumb, label) {
  return (
    <Flyout flyout={<StructureDataContainer breadcrumb={breadcrumb} />} inline theme="simple" trigger="hover">
      {label}
    </Flyout>
  );
}

function getBreadcrumbHref(breadcrumb) {
  return breadcrumb.get('isHeatmapShown')
    ? generateHeatmapPath({
        ...breadcrumb.toJS(),
        resultName: breadcrumb.get('heatmapResultName'),
        context: dpitems,
        isHeatmap: true,
      })
    : generateResultsPath(breadcrumb.toJS());
}

function renderCurrentItem(breadcrumb) {
  const label = getBreadcrumbLabel(breadcrumb);
  return {
    type: 'current',
    label: getBreadcrumbContent(breadcrumb, label),
  };
}

function renderLinkItem(breadcrumb, goToResultPage) {
  const label = getBreadcrumbLabel(breadcrumb);
  const href = getBreadcrumbHref(breadcrumb);
  return {
    type: 'link',
    linkElementProps: {
      children: getBreadcrumbContent(breadcrumb, label),
      className: 'c-els-color-info',
      onClick: (e) => {
        e.preventDefault();
        if (!isClickWithinTooltip(e)) {
          goToResultPage(breadcrumb.toJS());
        }
      },
      href,
      routerComponent: Link,
      'data-automationid': `breadcrumb-link`,
    },
  };
}

function renderPreviewResultsItem(breadcrumb, backToQueryHandler) {
  return {
    type: 'link',
    linkElementProps: {
      children: 'Preview results',
      className: 'c-els-color-info',
      onClick: (e) => {
        e.preventDefault();
        if (!isClickWithinTooltip(e)) {
          backToQueryHandler(breadcrumb.toJS(), BACK_TO_QUERY_QUICK_SEARCH_ACTIONS.PREVIEW);
        }
      },
      href: paths.PREVIEW,
      routerComponent: Link,
      'data-automationid': 'breadcrumb-preview-results',
    },
  };
}

function renderSearchItem(breadcrumb, backToQueryHandler, isBackToQueryBuilder) {
  return {
    type: 'link',
    linkElementProps: {
      children: getBreadcrumbContent(breadcrumb, 'Search'),
      className: 'c-els-color-info',
      onClick: (e) => {
        e.preventDefault();
        if (!isClickWithinTooltip(e)) {
          backToQueryHandler(breadcrumb.toJS(), BACK_TO_QUERY_QUICK_SEARCH_ACTIONS.EDIT);
        }
      },
      href: isBackToQueryBuilder ? paths.ADVANCED : paths.SEARCH,
      routerComponent: Link,
      'data-automationid': 'breadcrumb-search',
    },
  };
}

function Breadcrumbs({ breadcrumbsIds, breadcrumbsItems, goToResultPage, backToQueryHandler }) {
  const breadcrumbsData = useMemo(() => {
    if (!breadcrumbsIds || !breadcrumbsItems) return [];
    const idsArr = breadcrumbsIds.toJS();

    const breadcrumbsArr = idsArr.map((id) => {
      const breadcrumb = breadcrumbsItems.get(id);
      const isCurrent = breadcrumb.get('active');
      return isCurrent ? renderCurrentItem(breadcrumb) : renderLinkItem(breadcrumb, goToResultPage);
    });

    const backToQueryBreadcrumbId = breadcrumbsIds.first();
    const backToQueryBreadcrumbItem = breadcrumbsItems.get(backToQueryBreadcrumbId);
    const isBackToQueryVisible = backToQueryBreadcrumbItem && isBackToQueryVisibleFn(backToQueryBreadcrumbItem);
    const isBackToQueryBuilder = backToQueryBreadcrumbItem && isBackToQueryBuilderFn(backToQueryBreadcrumbItem);
    const shouldDisplayPreviewResultsBreadcrumb =
      backToQueryBreadcrumbItem && shouldDisplayPreviewResultsBreadcrumbFn(backToQueryBreadcrumbItem);

    if (isBackToQueryVisible) {
      // Preview results breadcrumb
      if (shouldDisplayPreviewResultsBreadcrumb) {
        breadcrumbsArr.unshift(renderPreviewResultsItem(backToQueryBreadcrumbItem, backToQueryHandler));
      }
      // Search breadcrumb
      breadcrumbsArr.unshift(renderSearchItem(backToQueryBreadcrumbItem, backToQueryHandler, isBackToQueryBuilder));
    }

    return breadcrumbsArr;
  }, [breadcrumbsIds, breadcrumbsItems]);

  return (
    <Breadcrumb
      className="u-els-padding-top u-els-padding-bottom u-els-padding-left-2x u-els-padding-right-2x u-els-background-color-n0 u-els-font-size-base results-breadcrumbs"
      itemData={breadcrumbsData}
    />
  );
}

export default Breadcrumbs;
